import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { 
  Loader2, Trophy, Search, Plus, Minus, Award, Shield, Crown, Star,
  Flame, Zap, Target, Medal, ChevronDown, ChevronUp, Gift, Trash2,
  TrendingUp, Users, Sparkles
} from "lucide-react";

// Icon mapping
const BADGE_ICONS = {
  flame: Flame, zap: Zap, trophy: Trophy, crown: Crown, star: Star,
  target: Target, award: Award, medal: Medal, shield: Shield, gem: Sparkles
};

// Tier badge component
const TierBadge = ({ tier, size = "sm" }) => {
  const styles = {
    bronze: { bg: "bg-gradient-to-br from-amber-700 to-amber-900", border: "border-amber-500", text: "text-amber-300" },
    silver: { bg: "bg-gradient-to-br from-gray-300 to-gray-500", border: "border-gray-300", text: "text-gray-100" },
    gold: { bg: "bg-gradient-to-br from-yellow-400 to-yellow-600", border: "border-yellow-400", text: "text-yellow-100" },
    og: { bg: "bg-gradient-to-br from-neon-pink via-purple-600 to-neon-blue", border: "border-neon-pink", text: "text-white" }
  };
  const s = styles[tier] || styles.bronze;
  const sizes = { sm: "w-8 h-8 text-[8px]", md: "w-10 h-10 text-xs" };
  const names = { bronze: "BRONZE", silver: "SILVER", gold: "GOLD", og: "O.G." };

  return (
    <div className={`${sizes[size]} rounded-lg ${s.bg} ${s.border} border-2 flex flex-col items-center justify-center`}>
      <Shield className={`w-3 h-3 ${s.text}`} />
      <span className={`font-orbitron font-bold ${s.text} leading-none`}>{names[tier]}</span>
    </div>
  );
};

// Mini badge display
const MiniBadge = ({ badge, onRemove }) => {
  const Icon = BADGE_ICONS[badge.icon] || Star;
  return (
    <div 
      className="group relative flex items-center gap-1 px-2 py-1 rounded-full text-xs"
      style={{ 
        background: `${badge.color}20`,
        border: `1px solid ${badge.color}50`
      }}
    >
      <Icon className="w-3 h-3" style={{ color: badge.color }} />
      <span className="font-rajdhani text-white/80">{badge.name}</span>
      {onRemove && (
        <button 
          onClick={(e) => { e.stopPropagation(); onRemove(badge.id); }}
          className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Trash2 className="w-3 h-3 text-red-400 hover:text-red-300" />
        </button>
      )}
    </div>
  );
};

const LeaderboardManager = () => {
  const { authAxios } = useAuth();
  const [players, setPlayers] = useState([]);
  const [availableBadges, setAvailableBadges] = useState({ bonus_badges: [], seasonal_badges: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [expandedPlayer, setExpandedPlayer] = useState(null);
  const [stats, setStats] = useState({ total: 0, tier_distribution: {} });
  
  // Modal states
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [awardModalOpen, setAwardModalOpen] = useState(false);
  const [addPlayerModalOpen, setAddPlayerModalOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [adjustAmount, setAdjustAmount] = useState(0);
  const [adjustReason, setAdjustReason] = useState("");
  const [selectedBadge, setSelectedBadge] = useState("");
  const [processing, setProcessing] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [initialBadges, setInitialBadges] = useState(0);
  const [newPlayerName, setNewPlayerName] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [playersRes, badgesRes] = await Promise.all([
        authAxios.get('/admin/leaderboard/players'),
        authAxios.get('/admin/leaderboard/available-badges')
      ]);
      setPlayers(playersRes.data.players || []);
      setStats({
        total: playersRes.data.total,
        tier_distribution: playersRes.data.tier_distribution
      });
      setAvailableBadges(badgesRes.data);
    } catch (e) {
      toast.error("Failed to fetch leaderboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustBadges = async () => {
    if (!selectedPlayer || adjustAmount === 0) return;
    setProcessing(true);
    try {
      const res = await authAxios.put('/admin/leaderboard/adjust-badges', {
        user_id: selectedPlayer.id,
        amount: adjustAmount,
        reason: adjustReason || undefined
      });
      toast.success(`Badges adjusted: ${res.data.old_badge_count} → ${res.data.new_badge_count}`);
      if (res.data.tier_changed) {
        toast.info(`Tier changed: ${res.data.old_tier.toUpperCase()} → ${res.data.new_tier.toUpperCase()}`);
      }
      setAdjustModalOpen(false);
      setAdjustAmount(0);
      setAdjustReason("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to adjust badges");
    } finally {
      setProcessing(false);
    }
  };

  const handleAwardBadge = async () => {
    if (!selectedPlayer || !selectedBadge) return;
    setProcessing(true);
    try {
      const res = await authAxios.post('/admin/leaderboard/award-badge', {
        user_id: selectedPlayer.id,
        badge_id: selectedBadge
      });
      toast.success(`Badge "${res.data.badge_awarded.name}" awarded! (+${res.data.bonus_points_added} points)`);
      setAwardModalOpen(false);
      setSelectedBadge("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to award badge");
    } finally {
      setProcessing(false);
    }
  };

  const handleRemoveBadge = async (playerId, badgeId) => {
    if (!window.confirm("Remove this badge from the player?")) return;
    try {
      await authAxios.delete(`/admin/leaderboard/remove-badge/${playerId}/${badgeId}`);
      toast.success("Badge removed");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove badge");
    }
  };

  const handleInitializePlayer = async (userId) => {
    try {
      await authAxios.post(`/admin/leaderboard/initialize-player/${userId}`);
      toast.success("Player initialized for leaderboard");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to initialize player");
    }
  };

  const handleDeletePlayer = async (playerId, playerName) => {
    if (!window.confirm(`Remove "${playerName}" from the leaderboard? This will reset all their badges, check-ins, and progress.`)) return;
    try {
      await authAxios.delete(`/admin/leaderboard/player/${playerId}`);
      toast.success(`${playerName} removed from leaderboard`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove player");
    }
  };

  const handleOpenAddPlayer = async () => {
    try {
      const res = await authAxios.get('/admin/users');
      setAllUsers(res.data || []);
      setAddPlayerModalOpen(true);
    } catch (e) {
      toast.error("Failed to fetch users");
      setAddPlayerModalOpen(true); // Still open modal for creating new players
    }
  };

  const handleCreateNewPlayer = async () => {
    if (!newPlayerName.trim()) {
      toast.error("Please enter a player name");
      return;
    }
    setProcessing(true);
    try {
      const res = await authAxios.post('/admin/leaderboard/create-player', {
        name: newPlayerName.trim(),
        initial_badges: initialBadges
      });
      toast.success(`${res.data.player.name} added to leaderboard with ${res.data.player.badge_count} badges`);
      setAddPlayerModalOpen(false);
      setNewPlayerName("");
      setInitialBadges(0);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create player");
    } finally {
      setProcessing(false);
    }
  };

  const handleAddPlayer = async (userId, userName) => {
    setProcessing(true);
    try {
      // First initialize the player
      await authAxios.post(`/admin/leaderboard/initialize-player/${userId}`);
      
      // Then add initial badges if specified
      if (initialBadges > 0) {
        await authAxios.put('/admin/leaderboard/adjust-badges', {
          user_id: userId,
          amount: initialBadges,
          reason: "Initial badges on leaderboard add"
        });
      }
      
      toast.success(`${userName} added to leaderboard${initialBadges > 0 ? ` with ${initialBadges} badges` : ''}`);
      setAddPlayerModalOpen(false);
      setInitialBadges(0);
      setUserSearchQuery("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add player");
    } finally {
      setProcessing(false);
    }
  };

  // Filter players
  const filteredPlayers = players.filter(p => {
    const matchesSearch = searchQuery === "" || 
      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTier = tierFilter === "all" || p.tier === tierFilter;
    return matchesSearch && matchesTier;
  });

  // Filter users not already on leaderboard (with badges)
  const filteredUsers = allUsers.filter(u => {
    const matchesSearch = userSearchQuery === "" || 
      u.name?.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(userSearchQuery.toLowerCase());
    // Show users not already on leaderboard (badge_count is 0 or undefined)
    const notOnLeaderboard = !players.some(p => p.id === u.id && p.badge_count > 0);
    return matchesSearch && notOnLeaderboard;
  });

  // Get badges player doesn't have yet
  const getUnownedBadges = (player) => {
    const owned = player.all_badge_ids || [];
    const bonus = availableBadges.bonus_badges.filter(b => !owned.includes(b.id));
    const seasonal = availableBadges.seasonal_badges.filter(b => !owned.includes(b.id));
    return [...bonus, ...seasonal];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-neon-pink animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
          <Trophy className="w-5 h-5 text-neon-pink" />
          Leaderboard Manager
        </h2>
        <button
          onClick={handleOpenAddPlayer}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-neon-green to-emerald-500 text-black font-orbitron text-sm hover:opacity-90 transition-opacity"
          data-testid="add-player-btn"
        >
          <Plus className="w-4 h-4" />
          Add Player
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="cyber-card p-4 text-center">
          <Users className="w-5 h-5 text-neon-blue mx-auto mb-1" />
          <p className="font-orbitron text-xl text-white">{stats.total}</p>
          <p className="font-rajdhani text-xs text-gray-500">Total Players</p>
        </div>
        {Object.entries(stats.tier_distribution).map(([tier, count]) => (
          <div key={tier} className="cyber-card p-4 text-center">
            <TierBadge tier={tier} size="sm" />
            <p className="font-orbitron text-lg text-white mt-2">{count}</p>
            <p className="font-rajdhani text-xs text-gray-500 capitalize">{tier}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-void-black border-white/10 text-white"
            data-testid="leaderboard-search"
          />
        </div>
        <Select value={tierFilter} onValueChange={setTierFilter}>
          <SelectTrigger className="w-full md:w-48 bg-void-black border-white/10 text-white" data-testid="tier-filter">
            <SelectValue placeholder="Filter by tier" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tiers</SelectItem>
            <SelectItem value="bronze">Bronze</SelectItem>
            <SelectItem value="silver">Silver</SelectItem>
            <SelectItem value="gold">Gold</SelectItem>
            <SelectItem value="og">O.G.</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Players List */}
      <div className="space-y-2">
        {filteredPlayers.length === 0 ? (
          <div className="cyber-card p-8 text-center">
            <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="font-orbitron text-gray-500">No players found</p>
          </div>
        ) : (
          filteredPlayers.map((player, index) => (
            <div 
              key={player.id}
              className="cyber-card overflow-hidden"
            >
              {/* Main Row */}
              <div 
                className="p-4 flex items-center gap-4 cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => setExpandedPlayer(expandedPlayer === player.id ? null : player.id)}
                data-testid={`player-row-${player.id}`}
              >
                {/* Rank */}
                <div className="w-8 text-center">
                  <span className="font-orbitron text-lg text-gray-400">{index + 1}</span>
                </div>

                {/* Tier Badge */}
                <TierBadge tier={player.tier} size="md" />

                {/* Player Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-orbitron text-white truncate">{player.name}</p>
                  <p className="font-rajdhani text-xs text-gray-500 truncate">{player.email}</p>
                </div>

                {/* Stats */}
                <div className="hidden md:flex items-center gap-6">
                  <div className="text-center">
                    <p className="font-orbitron text-lg text-neon-pink">{player.badge_count}</p>
                    <p className="font-rajdhani text-xs text-gray-500">Badges</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center gap-1">
                      <Flame className="w-4 h-4 text-orange-500" />
                      <span className="font-orbitron text-white">{player.streak}</span>
                    </div>
                    <p className="font-rajdhani text-xs text-gray-500">Streak</p>
                  </div>
                  <div className="text-center">
                    <p className="font-orbitron text-white">{player.total_checkins}</p>
                    <p className="font-rajdhani text-xs text-gray-500">Check-ins</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedPlayer(player); setAdjustModalOpen(true); }}
                    className="p-2 rounded-lg bg-neon-blue/20 text-neon-blue hover:bg-neon-blue/30 transition-colors"
                    title="Adjust badges"
                    data-testid={`adjust-badges-${player.id}`}
                  >
                    <TrendingUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedPlayer(player); setAwardModalOpen(true); }}
                    className="p-2 rounded-lg bg-neon-pink/20 text-neon-pink hover:bg-neon-pink/30 transition-colors"
                    title="Award badge"
                    data-testid={`award-badge-${player.id}`}
                  >
                    <Award className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeletePlayer(player.id, player.name); }}
                    className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                    title="Remove from leaderboard"
                    data-testid={`delete-player-${player.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {expandedPlayer === player.id ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedPlayer === player.id && (
                <div className="px-4 pb-4 border-t border-white/10 pt-4 bg-white/5">
                  {/* Mobile Stats */}
                  <div className="md:hidden grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-2 bg-white/5 rounded">
                      <p className="font-orbitron text-lg text-neon-pink">{player.badge_count}</p>
                      <p className="font-rajdhani text-xs text-gray-500">Badges</p>
                    </div>
                    <div className="text-center p-2 bg-white/5 rounded">
                      <p className="font-orbitron text-lg text-orange-500">{player.streak}</p>
                      <p className="font-rajdhani text-xs text-gray-500">Streak</p>
                    </div>
                    <div className="text-center p-2 bg-white/5 rounded">
                      <p className="font-orbitron text-lg text-white">{player.total_checkins}</p>
                      <p className="font-rajdhani text-xs text-gray-500">Check-ins</p>
                    </div>
                  </div>

                  {/* Earned Badges */}
                  <div className="mb-4">
                    <p className="font-orbitron text-xs text-gray-400 mb-2">EARNED BADGES</p>
                    <div className="flex flex-wrap gap-2">
                      {player.bonus_badges?.length > 0 || player.seasonal_badges?.length > 0 ? (
                        <>
                          {player.bonus_badges?.map(badge => (
                            <MiniBadge 
                              key={badge.id} 
                              badge={badge} 
                              onRemove={(badgeId) => handleRemoveBadge(player.id, badgeId)}
                            />
                          ))}
                          {player.seasonal_badges?.map(badge => (
                            <MiniBadge 
                              key={badge.id} 
                              badge={badge}
                              onRemove={(badgeId) => handleRemoveBadge(player.id, badgeId)}
                            />
                          ))}
                        </>
                      ) : (
                        <span className="font-rajdhani text-gray-500 text-sm">No special badges yet</span>
                      )}
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-2">
                    {player.badge_count === 0 && player.total_checkins === 0 && (
                      <button
                        onClick={() => handleInitializePlayer(player.id)}
                        className="px-3 py-1.5 rounded-lg bg-neon-green/20 text-neon-green hover:bg-neon-green/30 transition-colors font-rajdhani text-sm flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" />
                        Initialize Player
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Adjust Badges Modal */}
      <Dialog open={adjustModalOpen} onOpenChange={setAdjustModalOpen}>
        <DialogContent className="bg-void-black border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-orbitron flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-neon-blue" />
              Adjust Badges - {selectedPlayer?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="text-center mb-6">
              <p className="font-rajdhani text-gray-400 text-sm">Current Badge Count</p>
              <p className="font-orbitron text-4xl text-neon-pink">{selectedPlayer?.badge_count || 0}</p>
            </div>
            
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => setAdjustAmount(a => a - 1)}
                className="w-12 h-12 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/30 flex items-center justify-center"
              >
                <Minus className="w-6 h-6" />
              </button>
              <div className="w-24 text-center">
                <Input
                  type="number"
                  value={adjustAmount}
                  onChange={(e) => setAdjustAmount(parseInt(e.target.value) || 0)}
                  className="text-center text-2xl font-orbitron bg-white/5 border-white/20"
                  data-testid="adjust-amount-input"
                />
              </div>
              <button
                onClick={() => setAdjustAmount(a => a + 1)}
                className="w-12 h-12 rounded-full bg-green-500/20 text-green-400 hover:bg-green-500/30 flex items-center justify-center"
              >
                <Plus className="w-6 h-6" />
              </button>
            </div>

            <div className="text-center">
              <p className="font-rajdhani text-gray-400 text-sm">New Badge Count</p>
              <p className="font-orbitron text-2xl text-white">
                {Math.max(0, (selectedPlayer?.badge_count || 0) + adjustAmount)}
              </p>
            </div>

            <Input
              placeholder="Reason (optional)"
              value={adjustReason}
              onChange={(e) => setAdjustReason(e.target.value)}
              className="bg-white/5 border-white/20"
              data-testid="adjust-reason-input"
            />

            <button
              onClick={handleAdjustBadges}
              disabled={processing || adjustAmount === 0}
              className="w-full py-3 rounded-lg bg-gradient-to-r from-neon-blue to-neon-pink text-white font-orbitron disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="confirm-adjust-btn"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Apply Adjustment
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Award Badge Modal */}
      <Dialog open={awardModalOpen} onOpenChange={setAwardModalOpen}>
        <DialogContent className="bg-void-black border-white/10 text-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-orbitron flex items-center gap-2">
              <Award className="w-5 h-5 text-neon-pink" />
              Award Badge - {selectedPlayer?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {selectedPlayer && getUnownedBadges(selectedPlayer).length === 0 ? (
              <div className="text-center py-8">
                <Gift className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="font-orbitron text-gray-500">Player has all badges!</p>
              </div>
            ) : (
              <>
                <p className="font-rajdhani text-gray-400 text-sm">Select a badge to award:</p>
                <div className="grid grid-cols-2 gap-2 max-h-[50vh] overflow-y-auto pr-2">
                  {selectedPlayer && getUnownedBadges(selectedPlayer).map(badge => {
                    const Icon = BADGE_ICONS[badge.icon] || Star;
                    return (
                      <button
                        key={badge.id}
                        onClick={() => setSelectedBadge(badge.id)}
                        className={`p-3 rounded-lg border transition-all text-left ${
                          selectedBadge === badge.id 
                            ? 'border-neon-pink bg-neon-pink/20' 
                            : 'border-white/10 bg-white/5 hover:border-white/30'
                        }`}
                        data-testid={`badge-option-${badge.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="w-5 h-5" style={{ color: badge.color }} />
                          <span className="font-orbitron text-xs text-white truncate">{badge.name}</span>
                        </div>
                        <p className="font-rajdhani text-[10px] text-gray-500 truncate">{badge.description}</p>
                        <p className="font-rajdhani text-xs text-neon-pink mt-1">+{badge.bonus_points} pts</p>
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={handleAwardBadge}
                  disabled={processing || !selectedBadge}
                  className="w-full py-3 rounded-lg bg-gradient-to-r from-neon-pink to-purple-600 text-white font-orbitron disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-award-btn"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Award Badge
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Player Modal */}
      <Dialog open={addPlayerModalOpen} onOpenChange={setAddPlayerModalOpen}>
        <DialogContent className="bg-void-black border-white/10 text-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-orbitron flex items-center gap-2">
              <Plus className="w-5 h-5 text-neon-green" />
              Add Player to Leaderboard
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Create New Player Section */}
            <div className="p-4 border-2 border-neon-green/30 rounded-lg bg-neon-green/5">
              <p className="font-orbitron text-sm text-neon-green mb-3">CREATE NEW PLAYER</p>
              <div className="flex gap-2">
                <Input
                  placeholder="Enter player name..."
                  value={newPlayerName}
                  onChange={(e) => setNewPlayerName(e.target.value)}
                  className="flex-1 bg-white/5 border-white/20"
                  data-testid="new-player-name-input"
                />
                <button
                  onClick={handleCreateNewPlayer}
                  disabled={processing || !newPlayerName.trim()}
                  className="px-4 py-2 rounded-lg bg-neon-green text-black font-orbitron text-sm hover:bg-neon-green/80 disabled:opacity-50 flex items-center gap-1"
                  data-testid="save-new-player-btn"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Save
                </button>
              </div>
            </div>

            {/* Starting Badges */}
            <div className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
              <span className="font-rajdhani text-gray-400 text-sm">Starting badges:</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setInitialBadges(b => Math.max(0, b - 1))}
                  className="w-8 h-8 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/30 flex items-center justify-center"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="font-orbitron text-xl text-white w-12 text-center">{initialBadges}</span>
                <button
                  onClick={() => setInitialBadges(b => b + 1)}
                  className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 hover:bg-green-500/30 flex items-center justify-center"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-white/10"></div>
              <span className="font-rajdhani text-gray-500 text-xs">OR ADD EXISTING USER</span>
              <div className="flex-1 h-px bg-white/10"></div>
            </div>

            {/* Search Existing Users */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                placeholder="Search users by name or email..."
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                className="pl-10 bg-white/5 border-white/20"
                data-testid="user-search-input"
              />
            </div>

            {/* Users List */}
            <div className="max-h-[30vh] overflow-y-auto space-y-2">
              {filteredUsers.length === 0 ? (
                <div className="text-center py-4">
                  <p className="font-rajdhani text-gray-500 text-sm">No existing users to add</p>
                </div>
              ) : (
                filteredUsers.map(user => (
                  <div 
                    key={user.id}
                    className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-orbitron text-sm text-white truncate">{user.name}</p>
                      <p className="font-rajdhani text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    <button
                      onClick={() => handleAddPlayer(user.id, user.name)}
                      disabled={processing}
                      className="px-4 py-2 rounded-lg bg-neon-blue/20 text-neon-blue hover:bg-neon-blue/30 font-orbitron text-xs flex items-center gap-1 disabled:opacity-50"
                      data-testid={`add-user-${user.id}`}
                    >
                      {processing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                      Add
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaderboardManager;
